import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { categories, MAIN_SITE_URL } from "@/mocks/categories";
import { BUYING_GUIDE_IMAGE } from "@/mocks/articles";
import JsonLd from "@/components/seo/JsonLd";
import { SITE_URL, SITE_LOGO_URL } from "@/components/seo/siteUrl";

export default function BuyingGuidePage() {
  const { t, i18n } = useTranslation();
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const steps = [
    { num: "01", icon: "ri-compass-3-line", title: t("buyingGuide.step1.title"), text: t("buyingGuide.step1.text") },
    { num: "02", icon: "ri-line-chart-line", title: t("buyingGuide.step2.title"), text: t("buyingGuide.step2.text") },
    { num: "03", icon: "ri-shopping-bag-3-line", title: t("buyingGuide.step3.title"), text: t("buyingGuide.step3.text") },
  ];

  const faqs = [1, 2, 3].map((n) => ({
    q: t(`buyingGuide.faq${n}.q`),
    a: t(`buyingGuide.faq${n}.a`),
  }));

  const webPageSchema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: t("buyingGuide.metaTitle"),
    description: t("buyingGuide.metaDescription"),
    url: `${SITE_URL}/buying-guide`,
    inLanguage: i18n.language,
    publisher: {
      "@type": "Organization",
      name: "USFans Spreadsheet",
      logo: { "@type": "ImageObject", url: SITE_LOGO_URL },
    },
  };

  const faqSchema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd data={webPageSchema} />
      <JsonLd data={faqSchema} />
      <Navbar />
      <main className="pt-20">
        {/* Hero */}
        <section className="relative w-full overflow-hidden">
          <div className="relative h-[360px] md:h-[440px] w-full">
            <img
              src={BUYING_GUIDE_IMAGE}
              alt={t("buyingGuide.h1")}
              title={t("buyingGuide.h1")}
              className="absolute inset-0 h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/40 to-black/55"></div>
            <div className="relative z-10 mx-auto flex h-full max-w-7xl flex-col items-center justify-center px-4 md:px-6 text-center">
              <span className="inline-flex items-center gap-2 rounded-full border border-background-50/30 bg-background-50/10 px-4 py-1.5 text-sm font-medium text-background-50">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-shopping-cart-2-line"></i>
                </span>
                {t("buyingGuide.badge")}
              </span>
              <h1 className="mt-5 max-w-3xl font-heading text-3xl font-bold leading-tight text-background-50 md:text-5xl">
                {t("buyingGuide.h1")}
              </h1>
              <p className="mt-4 max-w-2xl text-base leading-relaxed text-background-100 md:text-lg">
                {t("buyingGuide.subtitle")}
              </p>
            </div>
          </div>
        </section>

        {/* Intro */}
        <section className="bg-background-50">
          <div className="mx-auto max-w-7xl px-4 md:px-6 py-10 md:py-14">
            <p className="mx-auto max-w-3xl text-center text-base leading-relaxed text-foreground-600 md:text-lg">
              {t("buyingGuide.intro")}
            </p>
          </div>
        </section>

        {/* Steps */}
        <section className="bg-background-100">
          <div className="mx-auto max-w-7xl px-4 md:px-6 py-12 md:py-16">
            <h2 className="text-center font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              {t("buyingGuide.stepsTitle")}
            </h2>
            <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-3">
              {steps.map((step) => (
                <div
                  key={step.num}
                  className="rounded-lg border border-background-200 bg-background-50 p-6"
                >
                  <span className="font-heading text-4xl font-bold text-accent-500">
                    {step.num}
                  </span>
                  <span className="mt-3 w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                    <i className={`${step.icon} text-lg`}></i>
                  </span>
                  <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">
                    {step.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                    {step.text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Categories */}
        <section className="bg-background-50">
          <div className="mx-auto max-w-7xl px-4 md:px-6 py-12 md:py-16">
            <div className="text-center">
              <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                {t("buyingGuide.categoriesTitle")}
              </h2>
              <p className="mt-2 text-base text-foreground-600">
                {t("buyingGuide.categoriesSubtitle")}
              </p>
            </div>
            <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              {categories.map((cat) => (
                <a
                  key={cat.key}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300 cursor-pointer"
                >
                  <div className="relative h-36 w-full overflow-hidden">
                    <img
                      src={cat.image}
                      alt={t(`category.${cat.key}`)}
                      title={`${t(`category.${cat.key}`)}`}
                      className="h-full w-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                    />
                  </div>
                  <div className="flex items-center justify-between gap-2 p-3">
                    <span className="text-sm font-medium text-foreground-900 whitespace-nowrap overflow-hidden text-ellipsis">
                      {t(`category.${cat.key}`)}
                    </span>
                    <span className="w-5 h-5 flex shrink-0 items-center justify-center rounded-full bg-background-100 text-foreground-500 transition-colors group-hover:bg-primary-500 group-hover:text-background-50">
                      <i className="ri-arrow-right-up-line text-sm"></i>
                    </span>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* Store CTA */}
        <section className="bg-background-100">
          <div className="mx-auto max-w-7xl px-4 md:px-6 py-12 md:py-16">
            <div className="rounded-2xl bg-primary-500 p-8 text-center md:p-14">
              <h2 className="font-heading text-2xl font-bold text-background-50 md:text-3xl">
                {t("buyingGuide.storeTitle")}
              </h2>
              <p className="mx-auto mt-3 max-w-xl text-base text-background-100">
                {t("buyingGuide.storeText")}
              </p>
              <a
                href={MAIN_SITE_URL}
                target="_blank"
                rel="noopener nofollow"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-background-50 px-8 py-3.5 text-sm font-semibold text-primary-700 transition-colors hover:bg-background-100 whitespace-nowrap cursor-pointer"
              >
                {t("buyingGuide.storeCta")}
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-up-line"></i>
                </span>
              </a>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="bg-background-50">
          <div className="mx-auto max-w-3xl px-4 md:px-6 py-12 md:py-16">
            <h2 className="text-center font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              {t("buyingGuide.faqTitle")}
            </h2>
            <div className="mt-8 space-y-3">
              {faqs.map((item, idx) => {
                const isOpen = openFaq === idx;
                return (
                  <div
                    key={item.q}
                    className="overflow-hidden rounded-lg border border-background-200 bg-background-50"
                  >
                    <button
                      type="button"
                      onClick={() => setOpenFaq(isOpen ? null : idx)}
                      className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left cursor-pointer"
                    >
                      <span className="font-heading text-sm font-semibold text-foreground-950 md:text-base">
                        {item.q}
                      </span>
                      <span className="w-6 h-6 flex shrink-0 items-center justify-center rounded-full bg-background-100 text-foreground-600">
                        <i
                          className={`ri-arrow-down-s-line transition-transform ${
                            isOpen ? "rotate-180" : ""
                          }`}
                        ></i>
                      </span>
                    </button>
                    {isOpen && (
                      <div className="px-5 pb-5">
                        <p className="text-sm leading-relaxed text-foreground-600">
                          {item.a}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="mt-10 text-center">
              <Link
                to="/"
                className="inline-flex items-center gap-2 rounded-full border border-primary-500 px-6 py-3 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-50 whitespace-nowrap cursor-pointer"
              >
                {t("buyingGuide.backHome")}
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-left-line"></i>
                </span>
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}